/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * CourseHeaderStyles.js
 * Styles for difficult topic components
 */

import { makeStyles } from '@material-ui/core';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

const useCourseHeaderStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,

    '& > *': {
      boxSizing: 'border-box'
    },
    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  },
  // Two columns container
  twoColumnsContainer: {
    flexDirection: 'column',
    backgroundColor: mixins.lightThemeBgColor2,
    padding: 16,
    borderRadius: 24,
    gap: 16,
    boxSizing: 'border-box',

    [mixins['mlg-min']]: {
      flexDirection: 'row',
      alignItems: 'stretch'
    },

    '& $courseCardTwoColumns': {
      flex: '1 0 0',
      width: '100%',

      [mixins['mlg-min']]: {
        flex: '0 0 416px',
        maxWidth: '416px'
      }
    },

    '& $book': {
      flex: '1 0 0',
      width: '100%',
      backgroundColor: `${mixins.chalkWhiteFont} !important`,
      borderRadius: '16px !important',
      padding: '12px !important',
      flexDirection: 'column',
      gap: 8,
      alignItems: 'flex-start',

      '& $coverImageContainer, & $download, & $coverImage': {
        [mixins['mlg-min']]: {
          minWidth: '128.25px',
          width: '128.25px',
          height: '171px',
          maxHeight: '171px'
        },
        [mixins['lg-min']]: {
          minWidth: '135px',
          width: '135px',
          height: '180px',
          maxHeight: '180px'
        }
      },

      [mixins['md-sm-min']]: {
        flexDirection: 'row'
      }
    }
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius: '12px',
    backgroundColor: mixins.chalkWhiteFont
  },
  courseCard: {
    '&&': {
      backgroundColor: mixins.lightThemeBgColor2,
      borderRadius: 24
    }
  },
  courseCardTwoColumns: {
    '&&': {
      backgroundColor: mixins.chalkWhiteFont,
      borderRadius: 16,
      padding: 12,
      gap: 12,
      flexDirection: 'column',
      justifyContent: 'space-between'
    },

    '& $courseCardContent': {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      width: '100%',
      flex: '1 0 0',
      padding: 4
    },

    '& $courseTitle': {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.48px',

      [mixins['mlg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      },
      [mixins['lg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      }
    },

    '& $metaDataContainer': {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      alignItems: 'center',
      width: '100%'
    },

    '& $metaDataItem': {
      display: 'inline-flex',
      alignItems: 'center',
      padding: 4,
      borderRadius: 4,
      border: '1px solid rgba(13, 0, 77, 0.45)',
      backgroundColor: mixins.chalkWhiteFont,
      whiteSpace: 'nowrap'
    },

    '& $metaDataLabel': {
      color: mixins.color_text_primary,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '22px'
    },

    '& $metaDataValue': {
      color: mixins.color_text_primary,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: '22px'
    },

    '& $footerContainer': {
      marginTop: 'auto',
      alignItems: 'center',
      paddingTop: 0
    },

    '& $inviteStudentsButton': {
      color: mixins.amethyst,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: '24px',
      textTransform: 'none',
      padding: 0,
      minWidth: 'unset',
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: 'transparent',
        color: mixins.linkHoverColor,
        textDecoration: 'underline'
      }
    },

    '& $courseMenuIcon': {
      width: 24,
      height: 24,
      minWidth: 24,
      padding: 4,
      borderRadius: '1000px',
      border: 'none',
      marginTop: 0,
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: mixins.secondaryButtonHoverColor
      },

      '& > span, & svg': {
        width: 16,
        height: 16
      }
    }
  },
  courseCardContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  metaDataContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4
  },
  metaDataItem: {
    display: 'inline-flex',
    alignItems: 'center'
  },
  metaDataLabel: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '22px'
  },
  metaDataValue: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px'
  },
  inviteStudentsButton: {
    textTransform: 'none'
  },
  courseChildrenContent: {
    marginTop: 16,
    width: '100%'
  },
  courseTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'baseline',
    gap: 4,

    [mixins['md-sm-min']]: {
      alignItems: 'flex-start'
    }
  },
  courseTitle: {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '30px',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [mixins['mlg-min']]: {
      fontSize: '28px',
      lineHeight: '35px',
      letterSpacing: '-0.54px'
    },
    [mixins['lg-min']]: {
      fontSize: '32px',
      lineHeight: '40px',
      letterSpacing: '-0.64px'
    }
  },
  bookTitle: {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '27px',

    [mixins['mlg-min']]: {
      fontSize: '24px',
      lineHeight: '29px',
      letterSpacing: '-0.4px'
    },
    [mixins['xl-min']]: {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.4px'
    }
  },
  instructorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  avatar: {
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',

    '&:has(span)': {
      border: `2px solid ${mixins.navyColor}`
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  },
  instructorName: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  },
  infoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-end',
    marginTop: 4
  },
  actionsContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',

    [mixins['md-sm-min']]: {
      width: 'auto'
    }
  },
  secondaryButton: {
    ...CommonStyles().secondaryButton,
    maxWidth: '100%',
    width: '100%',
    justifyContent: 'center',

    [mixins['md-sm-min']]: {
      maxWidth: '145px'
    }
  },
  secondaryGuidesButtonText: {
    ...CommonStyles().secondaryButtonText,
    fontWeight: 600,
    color: mixins.amethyst,
    padding: 0,
    gap: 0,

    '&:hover': {
      color: mixins.linkHoverColor
    }
  },
  secondaryGuidesButton: {
    ...CommonStyles().secondaryButton,
    border: 'none',
    padding: 0,
    height: 'inherit',
    maxHeight: '22px',

    '&:hover': {
      background: 'none'
    },
    [mixins['md-sm-min']]: {
      flexGrow: '1',
      alignSelf: 'center'
    }
  },
  educatorGuidesWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-end'
  },
  secondaryButtonText: {
    ...CommonStyles().secondaryButtonText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  focusVisible: {
    ...CommonStyles().focusVisible
  },
  iconButton: {
    ...CommonStyles().iconButton,
    border: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative'
  },
  hotspot: {
    width: 6,
    height: 6,
    backgroundColor: mixins.accentHotspotColor,
    position: 'absolute',
    top: 0,
    right: 5,
    borderRadius: '50%'
  },
  book: {
    flexDirection: 'row',
    padding: '8px !important',
    gap: 12,
    borderRadius: 16,

    [mixins['md-sm-min']]: {
      padding: '12px !important'
    }
  },
  coverImageContainer: {
    minWidth: '108px',
    maxHeight: '144px',

    [mixins['md-sm-min']]: {
      minWidth: '109.5px',
      maxHeight: '146px'
    },
    [mixins['mlg-min']]: {
      minWidth: '90.75px',
      maxHeight: '121px'
    },
    [mixins['lg-min']]: {
      minWidth: '93px',
      maxHeight: '124px'
    }
  },
  coverImage: {
    width: '108px',
    height: '144px',
    flexShrink: '0',
    aspectRatio: '3/4',
    objectFit: 'cover',
    borderRadius: 8,

    [mixins['md-sm-min']]: {
      width: '109.5px',
      height: '146px'
    },
    [mixins['mlg-min']]: {
      width: '90.75px',
      height: '121px'
    },
    [mixins['lg-min']]: {
      width: '93px',
      height: '124px'
    }
  },
  download: {
    '&&': {
      width: '108px',
      height: '144px',
      flexShrink: '0',
      aspectRatio: '3/4',
      objectFit: 'cover',
      borderRadius: 8,

      [mixins['md-sm-min']]: {
        width: '109.5px',
        height: '146px'
      },
      [mixins['mlg-min']]: {
        width: '90.75px',
        height: '121px'
      },
      [mixins['lg-min']]: {
        width: '93px',
        height: '124px'
      }
    }
  },
  author: {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    marginBottom: 8,

    [mixins['mlg-min']]: {
      marginBottom: 0
    }
  },
  bookInfo: {
    display: 'flex',
    padding: '4px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    flex: '1 0 0',
    alignSelf: 'stretch'
  },
  goToPageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',

    '& .MuiInputBase-input::placeholder': {
      fontSize: 10
    },

    [mixins['md-sm-min']]: {
      flexDirection: 'row',
      alignItems: 'center',
      maxHeight: 40,
      gap: '8px',
      width: 'auto'
    }
  },
  goToPageLabel: {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  },
  goToTextField: {
    maxHeight: 40,
    width: '100%',
    padding: '8px 12px 8px 12px',
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    boxSizing: 'border-box',
    maxWidth: '100%',

    '& .MuiInput-formControl': {
      color: mixins.greyScaleColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '24px',

      '&::before, &::after': {
        content: 'none'
      }
    },
    '& .MuiInput-input': {
      padding: 0,
      height: 'inherit',
      textAlign: 'center'
    },
    '&:focus-within': {
      outline: `1px solid ${mixins.amethyst}`,
      marginLeft: 2,

      '& .MuiInput-formControl': {
        color: mixins.color_text_primary
      }
    },

    [mixins['md-sm-min']]: {
      maxWidth: '80px'
    }
  },
  bookActionsContainer: {
    display: 'flex',
    gap: '12px',
    width: '100%',

    [mixins['md-sm-min']]: {
      height: '100%',
      alignItems: 'flex-end'
    }
  },
  bookActions: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flexGrow: 1,
    gap: 12,
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  },
  scheduleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  scheduleTitle: {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  },
  scheduleItem: {
    display: 'flex'
  },
  scheduleItemTitle: {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  },
  scheduleItemsContainer: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  scheduleItemTime: {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 0
  },
  titleInfoContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%',
    justifyContent: 'space-between'
  },
  courseMenuIcon: {
    padding: 4,
    border: 'none',
    width: 24,
    height: 24,
    transition: 'transform 0.3s ease-in-out',
    marginLeft: 'auto',
    justifyContent: 'flex-end',

    [mixins['md-sm-min']]: {
      marginTop: 6
    },

    '& > span, & svg': {
      width: 16,
      height: 16
    }
  },
  activeCourseMenuIcon: {
    transform: 'rotate(-90deg)'
  },
  courseMenuRoot: {
    '&&': {
      padding: '8px !important'
    }
  },
  courseMenuItem: {
    padding: 8,

    '& p': {
      color: mixins.timeIconColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '22px'
    },

    '&:hover': {
      backgroundColor: mixins.quaternary
    }
  },
  icon: {
    width: '16px',
    height: '16px'
  },
  iconCtaButton: {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: '8px 0px 8px 1px',
    borderRadius: '1000px',
    minWidth: 'unset',
    height: 'fit-content',
    whiteSpace: 'nowrap',

    [mixins['md-sm-min']]: {
      padding: 8
    },
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& > span': {
      minWidth: 'unset',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 8px 2px 2px',

      [mixins['md-sm-min']]: {
        padding: '0px 8px 2px 8px'
      }
    },
    '& > span > span': {
      marginRight: 4,
      marginTop: 1
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  },
  actionContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [mixins['md-sm-min']]: {
      width: '100%',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      alignItems: 'unset'
    }
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center'
  },
  courseMenuItemDivider: {
    borderTop: `1px solid ${mixins.color_elevation_alpha_12}`
  },
  unifiedContainer: {
    '&&': {
      backgroundColor: mixins.lightThemeBgColor2,
      borderRadius: 24,
      padding: 16,
      gap: 16
    }
  },
  unifiedCard: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 16,
    boxSizing: 'border-box'
  },
  unifiedCardMain: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: '1 1 auto',

    [mixins['lg-min']]: {
      position: 'relative',
      minHeight: 128
    }
  },
  unifiedCardBody: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "cover"
      "content"
      "actions"
    `,
    rowGap: 12,
    width: '100%',
    alignItems: 'start',

    [mixins['md-sm-min']]: {
      gridTemplateColumns: 'auto 1fr auto',
      gridTemplateAreas: 'none',
      columnGap: 24,
      rowGap: 8
    }
  },
  unifiedCoverContainer: {
    position: 'relative',
    flexShrink: 0,
    width: 96,
    height: 128,
    gridArea: 'cover',
    alignSelf: 'start',
    overflow: 'visible',

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '1',
      gridRow: '1'
    }
  },
  unifiedCoverImage: {
    width: 96,
    height: 128,
    objectFit: 'cover',
    borderRadius: 4,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
    display: 'block'
  },
  unifiedDownload: {
    '&&': {
      width: 96,
      height: 128,
      borderRadius: 4,
      boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)'
    }
  },
  unifiedCoverInfoButton: {
    ...CommonStyles().iconButton,
    position: 'absolute',
    right: -12,
    bottom: -12,
    width: 24,
    height: 24,
    minWidth: 24,
    padding: 4,
    borderRadius: '50%',
    backgroundColor: mixins.chalkWhiteFont,
    border: '0.556px solid rgba(0, 0, 0, 0.10)',
    boxShadow: '0px 4px 3px rgba(0, 0, 0, 0.10), 0px 2px 2px rgba(0, 0, 0, 0.10)',
    zIndex: 1,

    '& svg': {
      width: 16,
      height: 16
    }
  },
  unifiedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gridArea: 'content',
    alignSelf: 'start',
    minWidth: 0,

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '2',
      gridRow: '1'
    }
  },
  unifiedCourseTitle: {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    width: '100%',
    minWidth: 0,
    ...CommonStyles().truncateNumberOfLines(3),

    [mixins['lg-min']]: {
      display: 'block',
      overflow: 'visible',
      textOverflow: 'unset',
      WebkitBoxOrient: 'unset',
      WebkitLineClamp: 'unset',
      lineClamp: 'unset'
    }
  },
  unifiedMetaRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
    width: '100%',

    [mixins['md-sm-min']]: {
      flexWrap: 'nowrap',
      gap: 12,
      paddingTop: 8
    }
  },
  unifiedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: '#EFEDFF',
    color: mixins.amethyst,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  unifiedDateRange: {
    color: mixins.greyScaleColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  unifiedBookTitle: {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    paddingTop: 16,
    width: '100%',
    minWidth: 0,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    ...CommonStyles().truncateNumberOfLines(3),

    [mixins['lg-min']]: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
      textOverflow: 'unset'
    }
  },
  unifiedActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
    gridArea: 'actions',
    width: '100%',
    justifySelf: 'stretch',
    justifyContent: 'flex-start',

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '3',
      gridRow: '1',
      alignSelf: 'start',
      justifySelf: 'end',
      width: 'auto'
    }
  },
  unifiedFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    marginTop: 'auto',
    paddingTop: 0,
    flexShrink: 0,

    [mixins['lg-min']]: {
      position: 'absolute',
      right: 0,
      top: 128,
      transform: 'translateY(-100%)',
      width: 'auto',
      marginTop: 0,
      paddingTop: 0
    },

    '& $inviteStudentsButton': {
      color: mixins.amethyst,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight600,
      lineHeight: '24px',
      textTransform: 'none',
      padding: 0,
      minWidth: 'unset',
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: 'transparent',
        color: mixins.linkHoverColor,
        textDecoration: 'underline'
      }
    }
  }
});

export default useCourseHeaderStyles;
